import { NavaidFeatureInfoData } from '@modules/MapScreen/components/MapNavaid/navaidFormatters'
import GeoJSON from 'geojson'
import { BehaviorSubject } from 'rxjs'

import { Coordinates } from '@/domain/protocols/Coordinates'
import { FuelData } from '@/domain/protocols/Fuel'

import { MeteorologyManifest } from '@/data/MeteorologyRepository/IMeteorologyRepository'

export interface ObstacleFeatureInfoData {
  obstacleId: string
  type?: string
  elevationTopFt?: number
  elevationBaseFt?: number
  lighted?: boolean
  coordinates: Coordinates
}

interface AirspaceFeature {
  type: 'airspace'
  title: string
  airspaceType?: string
  feature: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>
}

interface MeteorologyLegend {
  type: 'meteorology-legend'
  title: string
  legends: MeteorologyManifest['legends']
}

interface MeteorologyTooltip {
  type: 'meteorology-tooltip'
  title: string
  content: string
}

interface FuelFeature {
  type: 'fuel'
  title: string
  adhpCode: string
  providers: FuelData[]
}

interface ObstacleFeature {
  type: 'obstacle'
  title: string
  obstacle: ObstacleFeatureInfoData
}

interface UserGeometryFeature {
  type: 'user-geometry'
  title: string
  feature: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>
}
interface NavaidFeature {
  type: 'navaid'
  title: string
  navaid: NavaidFeatureInfoData
}

type FeatureType =
  | AirspaceFeature
  | MeteorologyLegend
  | MeteorologyTooltip
  | FuelFeature
  | ObstacleFeature
  | UserGeometryFeature
  | NavaidFeature

export interface FeatureInfoStateProps {
  active: FeatureType
}

const INITIAL_STATE: FeatureInfoStateProps = {
  active: null
}

export const FeatureInfoState = new BehaviorSubject<FeatureInfoStateProps>(INITIAL_STATE)

export const FeatureInfoStateMutator = {
  mutate: (newState: FeatureInfoStateProps) => {
    FeatureInfoState.next(newState)
  },

  showAirspace: (
    title: string,
    airspaceType?: string,
    feature?: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>
  ) => {
    FeatureInfoState.next({
      active: {
        type: 'airspace',
        title,
        airspaceType,
        feature
      }
    })
  },

  showMeteorologyLegend: (title: string, legends: MeteorologyManifest['legends']) => {
    FeatureInfoState.next({
      active: {
        type: 'meteorology-legend',
        title,
        legends
      }
    })
  },

  setInfo: (title: string, content: string) => {
    FeatureInfoState.next({
      active: {
        type: 'meteorology-tooltip',
        title,
        content
      }
    })
  },

  showFuel: (title: string, adhpCode: string, providers: FuelData[]) => {
    FeatureInfoState.next({
      active: {
        type: 'fuel',
        title,
        adhpCode,
        providers
      }
    })
  },

  showObstacle: (title: string, obstacle: ObstacleFeatureInfoData) => {
    FeatureInfoState.next({
      active: {
        type: 'obstacle',
        title,
        obstacle
      }
    })
  },

  showUserGeometry: (title: string, feature: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>) => {
    FeatureInfoState.next({
      active: {
        type: 'user-geometry',
        title,
        feature
      }
    })
  },
  showNavaid: (title: string, navaid: NavaidFeatureInfoData) => {
    FeatureInfoState.next({
      active: {
        type: 'navaid',
        title,
        navaid
      }
    })
  },

  hide: () => {
    FeatureInfoState.next({ active: null })
  }
}
